var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
export function generateUniqueId(prefix) {
    if (prefix === void 0) { prefix = 'component'; }
    // Current timestamp
    var timestamp = Date.now();
    // Random number to reduce the chance of collision
    var randomNumber = Math.floor(Math.random() * 10000);
    // Combine prefix, timestamp, and random number
    return "".concat(prefix, "-").concat(timestamp, "-").concat(randomNumber);
}
export function createAndApplyAttributes(elementType, attributesStr) {
    var element = document.createElement(elementType);
    // Split the attributes string by spaces, considering quotes to correctly handle spaces within attribute values
    var attributes = attributesStr.match(/([^\s="']+="[^"]*"|[^ ]+)/g);
    attributes.forEach(function (attr) {
        // Split each attribute by the first occurrence of "=" to separate the key and value
        var _a = attr.split(/=(.+)/), key = _a[0], value = _a[1];
        // Remove quotes from around the value, if present
        value = value.replace(/^['"]|['"]$/g, '');
        // Special handling for JSON-like syntax, convert it to valid JSON by replacing single quotes with double quotes
        if (value.startsWith("{") && value.endsWith("}")) {
            value = value.replace(/'/g, '"');
        }
        // Try to parse JSON values (for objects or arrays), otherwise keep the value as is
        try {
            value = JSON.parse(value);
        }
        catch (e) {
            // Value is not JSON, do nothing
        }
        // Set the attribute on the element
        element.setAttribute(key, value);
    });
    return element;
}
export function createElement(elementType, attributesDic) {
    var element = null;
    return function (moreAttr) {
        // Create the element
        element = element || document.createElement(elementType);
        // Split the attributes string by spaces, considering quotes to correctly handle spaces within attribute values
        Object.entries(attributesDic).forEach(function (attr) {
            // Split each attribute by the first occurrence of "=" to separate the key and value
            var key = attr[0], value = attr[1];
            // Set the attribute on the element
            element.setAttribute(key, value);
        });
        if (moreAttr) {
            Object.entries(moreAttr).forEach(function (attr) {
                // Split each attribute by the first occurrence of "=" to separate the key and value
                var key = attr[0], value = attr[1];
                // Set the attribute on the element
                element.setAttribute(key, value);
            });
        }
        return element;
    };
}
var Element = /** @class */ (function () {
    function Element(_a) {
        var element = _a.element, width = _a.width, height = _a.height, margin = _a.margin, _b = _a.alignment, alignment = _b === void 0 ? 'flexStart' : _b, _c = _a.direction, direction = _c === void 0 ? 'horizontal' : _c, onRender = _a.onRender;
        this.element = element;
        this.alignment = alignment;
        this.direction = direction;
        this.width = width;
        this.height = height;
        this.children = [];
        this.onRender = onRender;
        this.margin = __assign({ bottom: 0, left: 0, right: 0, top: 0 }, (margin || {}));
    }
    Element.prototype.appendChild = function () {
        var _a;
        var elements = [];
        for (var _i = 0; _i < arguments.length; _i++) {
            elements[_i] = arguments[_i];
        }
        (_a = this.children).push.apply(_a, elements);
    };
    Element.prototype.render = function (depth) {
        var _a;
        if (depth === void 0) { depth = 0; }
        var me = this;
        if (!((_a = this === null || this === void 0 ? void 0 : this.children) === null || _a === void 0 ? void 0 : _a.length)) {
            if (this.onRender) {
                this.onRender(this.element({}));
            }
            return {
                containerSize: {
                    width: this.width,
                    height: this.height
                },
                positions: [],
                element: this
            };
        }
        var items = this.children.map(function (element) {
            return element.render(depth + 1);
        });
        var _b = calculatePositionsAndContainerSize(items.map(function (t) { return t.containerSize; }), this.alignment, 'flexStart', this.direction, this.margin), containerSize = _b.containerSize, positions = _b.positions;
        var els = this.children.map(function (element, index) {
            var pos = positions[index];
            return position(element, "".concat(pos.x, " ").concat(pos.y, " ").concat(.02));
        });
        if (this.children.length) {
            this.width = containerSize.width;
            this.height = containerSize.height;
            var myEl_1 = me.element({ width: me.width, height: me.height });
            els.forEach(function (el) {
                myEl_1.appendChild(el);
            });
        }
        if (this.onRender) {
            this.onRender(this.element({}));
        }
        return {
            containerSize: containerSize,
            positions: positions,
            element: this
        };
    };
    return Element;
}());
export { Element };
export function position(element, position) {
    var container = createElement('a-entity', { position: "".concat(position) });
    var el = new Element({
        element: container,
        height: element.height,
        width: element.width
    });
    var temp = el.element();
    temp.appendChild(element.element());
    return temp;
}
function defaultMargin() {
    return {
        bottom: 0,
        left: 0,
        right: 0,
        top: 0,
    };
}
export function createContainer(type, attributes, margin) {
    if (type === void 0) { type = 'a-entity'; }
    var element = createElement(type, attributes);
    return new Element({
        element: element,
        height: 0,
        width: 0,
        margin: __assign(__assign({}, (defaultMargin())), (margin || {}))
    });
}
export function createComponent(type, attributes, height, width, margin, onRender) {
    if (type === void 0) { type = 'a-entity'; }
    var element = createElement(type, attributes);
    return new Element({
        element: element,
        height: height,
        width: width,
        margin: __assign(__assign({}, (defaultMargin())), (margin || {})),
        onRender: onRender
    });
}
export function createBaseInteractive(_a) {
    var _b;
    var value = _a.value, _c = _a.height, height = _c === void 0 ? .4 : _c, _d = _a.width, width = _d === void 0 ? .8 : _d, _e = _a.type, type = _e === void 0 ? "button" : _e, _f = _a.fontSize, fontSize = _f === void 0 ? .17 : _f;
    var element = createElement('frame-base-interactive', (_b = {
            width: width,
            height: height,
            value: value
        },
        _b['interactive-type'] = type,
        _b['font-size'] = fontSize,
        _b));
    return new Element({
        element: element,
        height: height,
        width: width
    });
}
export function createBaseIconInteractive(_a) {
    var _b;
    var _c = _a.value, value = _c === void 0 ? '' : _c, _d = _a.height, height = _d === void 0 ? .4 : _d, _e = _a.width, width = _e === void 0 ? .8 : _e, _f = _a.icon, icon = _f === void 0 ? 'f103' : _f, _g = _a.fontSize, fontSize = _g === void 0 ? .17 : _g, _h = _a.type, type = _h === void 0 ? 'icon-button' : _h;
    var element = createElement('frame-base-interactive', (_b = {
            width: width,
            height: height,
            value: value || icon
        },
        _b['interactive-type'] = type,
        _b['font-size'] = fontSize,
        _b.margin = "0 0 0.05 0",
        _b));
    return new Element({
        element: element,
        height: height,
        width: width
    });
    //  return createAndApplyAttributes('frame-base-interactive', `width="${width || .4}" height="${height || 0.4}" interactive-type="icon-button" value="${icon}" margin="0 0 0.05 0" font-size=".17"`)
}
export function onClick(el, func) {
    var handler = function () {
        if (func) {
            func();
        }
    };
    el.addEventListener('click', handler);
    return handler;
}
export function createMenuItem(_a) {
    var _b;
    var type = _a.type, _c = _a.width, width = _c === void 0 ? 1 : _c, text = _a.text, onRender = _a.onRender;
    var button = createElement("frame-base-interactive", (_b = {
            width: width
        },
        _b['interactive-type'] = type || 'button',
        _b.height = .2,
        _b.value = text,
        _b['font-size'] = .07,
        _b));
    return new Element({
        element: button,
        width: 1,
        height: .2,
        onRender: onRender
    });
}
export function createInteractiveButton(_a) {
    var _b;
    var interactiveType = _a.interactiveType, width = _a.width, text = _a.text, onRender = _a.onRender;
    var button = createElement("frame-base-interactive", (_b = {
            width: width || '1'
        },
        _b['interactive-type'] = interactiveType || 'button',
        _b.height = .2,
        _b.value = text,
        _b['font-size'] = .07,
        _b));
    return new Element({
        element: button,
        width: width || 1,
        height: .2,
        onRender: onRender
    });
}
export function createMenu(_a) {
    var _b;
    var _c = _a.text, text = _c === void 0 ? 'Menu' : _c, _d = _a.height, height = _d === void 0 ? .2 : _d, _e = _a.width, width = _e === void 0 ? 1 : _e, _f = _a.forwardStep, forwardStep = _f === void 0 ? .01 : _f, _g = _a.children, children = _g === void 0 ? [] : _g;
    var menu = createElement('frame-menu-container', (_b = {},
        _b["menu-direction"] = "up",
        _b["flex-direction"] = "column",
        _b["justify-content"] = "center",
        _b["align-items"] = "center",
        _b["component-padding"] = "0.01",
        _b['forward-step'] = forwardStep,
        _b.width = width,
        _b["text-value"] = text,
        _b["menu-item-height"] = height,
        _b["menu-item-width"] = width,
        _b))({});
    // menu.addEventListener('open-changed', (evt) => {
    //     const { detail } = evt;
    //     menu.setAttribute('open', !detail.open);
    // })
    // menu.addEventListener('close-menu', (evt) => {
    //     const { detail } = evt;
    //     menu.setAttribute('open', false);
    // })
    children.map(function (c) {
        menu.appendChild(c.element({}));
        if (c.onRender) {
            c.onRender(c.element({}));
        }
    });
    return new Element({
        element: function () { return menu; },
        height: height,
        width: width
    });
}
export function calculatePositionsAndContainerSize(items, alignment, justifyContent, // This is used to determine the starting vertical position
direction, margin) {
    var maxContainerWidth = 0;
    var maxContainerHeight = 0;
    var totalSize = 0; // This will track the total size of items along the main axis
    // Calculate total container size and totalSize of items
    items.forEach(function (item, index) {
        if (direction === 'vertical') {
            var effectiveWidth = item.width + margin.left + margin.right;
            maxContainerWidth = Math.max(maxContainerWidth, effectiveWidth);
            totalSize += item.height + (index < items.length - 1 ? margin.bottom : 0); // Adjusted for correct margin calculation
        }
        else { // 'horizontal'
            var effectiveHeight = item.height + margin.top + margin.bottom;
            maxContainerHeight = Math.max(maxContainerHeight, effectiveHeight);
            totalSize += item.width + (index < items.length - 1 ? margin.right : 0);
        }
    });
    // Adjust totalSize for final margin (only once, not per item)
    if (direction === 'vertical') {
        totalSize += margin.top; // Adjusted to add top margin
        maxContainerHeight = totalSize;
    }
    else {
        totalSize += margin.left; // Adjusted for horizontal direction
        maxContainerWidth = totalSize;
    }
    var currentPosition = 0;
    // Calculate starting position based on justifyContent
    if (justifyContent === 'center') {
        currentPosition = -(totalSize / 2); // Start from center and go both ways
    }
    else if (justifyContent === 'flexEnd') {
        currentPosition = -totalSize; // Start from bottom
    }
    else { // Default to flexStart or other cases
        currentPosition = 0; // Start from top
    }
    var positions = [];
    // Adjust positions based on alignment and the total calculated container size
    items.forEach(function (item, index) {
        var x;
        var y;
        if (direction === 'vertical') {
            // Calculate x based on alignment
            switch (alignment) {
                case 'flexStart':
                    x = margin.left + item.width / 2;
                    break;
                case 'flexEnd':
                    x = maxContainerWidth - margin.right - item.width / 2;
                    break;
                case 'center':
                    x = maxContainerWidth / 2;
                    break;
            }
            // Calculate y, starting from top and moving downwards
            y = currentPosition + margin.top + item.height / 2;
            currentPosition += item.height + margin.bottom;
        }
        else {
            // Horizontal direction logic remains unchanged
            switch (alignment) {
                case 'flexStart':
                    y = margin.top + item.height / 2;
                    break;
                case 'flexEnd':
                    y = maxContainerHeight - margin.bottom - item.height / 2;
                    break;
                case 'center':
                    y = maxContainerHeight / 2;
                    break;
            }
            x = currentPosition + margin.left + item.width / 2;
            currentPosition += item.width + margin.right;
        }
        positions.push({
            x: x - (maxContainerWidth / 2),
            y: -y + (maxContainerHeight / 2)
        }); // Adjust y to invert direction
    });
    return {
        positions: positions,
        containerSize: {
            width: maxContainerWidth,
            height: maxContainerHeight
        }
    };
}
export function createText(text, _a) {
    var color = _a.color, fontFamily = _a.fontFamily;
    var textEntity = document.createElement("a-entity");
    textEntity.setAttribute('troika-text', "value: ".concat(text, "; \n                                        align:left; \n                                        anchor:left; \n                                        baseline:center;\n                                        letterSpacing:0;\n                                        color:").concat(color, ";\n                                        font:").concat(fontFamily || '', ";\n                                        fontSize:").concat('14px', ";\n                                        depthOffset:1;\n                                        maxWidth:611;\n                                        "));
    textEntity.setAttribute('position', "0 0 0.07");
    textEntity.setAttribute('scale', "0.005 0.005 0.005");
    return textEntity;
}
export function calculatePositionsAndContainerSize2(items, alignment, direction, margin) {
    var positions = [];
    var maxContainerWidth = 0;
    var maxContainerHeight = 0;
    var currentPosition = 0;
    items.forEach(function (item, index) {
        var x;
        var y;
        if (direction === 'vertical') {
            var effectiveWidth = item.width + margin.left + margin.right;
            switch (alignment) {
                case 'flexStart':
                    x = margin.left + item.width / 2;
                    break;
                case 'flexEnd':
                    x = effectiveWidth - margin.right - item.width / 2;
                    break;
                case 'center':
                    x = effectiveWidth / 2;
                    break;
            }
            y = currentPosition + margin.top + item.height / 2;
            currentPosition += item.height + (index === 0 ? margin.top : 0) + margin.bottom;
            maxContainerWidth = Math.max(maxContainerWidth, effectiveWidth);
            maxContainerHeight = currentPosition;
        }
        else { // 'horizontal'
            var effectiveHeight = item.height + margin.top + margin.bottom;
            switch (alignment) {
                case 'flexStart':
                    y = margin.top + item.height / 2;
                    break;
                case 'flexEnd':
                    y = effectiveHeight - margin.bottom - item.height / 2;
                    break;
                case 'center':
                    y = effectiveHeight / 2;
                    break;
            }
            x = currentPosition + margin.left + item.width / 2;
            currentPosition += item.width + (index === 0 ? margin.left : 0) + margin.right;
            maxContainerHeight = Math.max(maxContainerHeight, effectiveHeight);
            maxContainerWidth = currentPosition;
        }
        positions.push({ x: x, y: y });
    });
    // Adjust final container size to include the margin on the far side
    if (direction === 'vertical') {
        maxContainerHeight += margin.bottom; // Add bottom margin only once at the end
    }
    else { // 'horizontal'
        maxContainerWidth += margin.right; // Add right margin only once at the end
    }
    return {
        positions: positions,
        containerSize: {
            width: maxContainerWidth,
            height: maxContainerHeight
        }
    };
}
export var containerStlye = {
    padding: '20px',
    backgroundColor: 'rgba(255, 255, 255, 0.8)',
    borderRadius: '8px',
    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
    maxWidth: 400,
    zIndex: 1000, // Ensure it's above other content
};
export var btnstyle = {
    display: 'inline-block',
    padding: '10px 15px',
    backgroundColor: '#007bff', // Bootstrap primary button color
    color: 'white',
    textAlign: 'center',
    textDecoration: 'none',
    border: '1px solid transparent',
    borderRadius: '4px',
    cursor: 'pointer',
    transition: 'background-color 0.2s',
};
export function findClosestValue(textValueMap, val) {
    // Initialize a variable to keep track of the closest item. Start with null and will replace it with an item from textValueMap.
    var closestItem = null;
    // Initialize a variable to keep track of the smallest difference found. Start with Infinity as we haven't checked any items yet.
    var smallestDifference = Infinity;
    // Iterate over each item in textValueMap
    textValueMap.forEach(function (item) {
        // Convert the item's value to a number for comparison
        var itemValue = (item.value);
        // Calculate the absolute difference between the item's value and the provided val
        var difference = Math.abs(val - itemValue);
        // Check if this difference is smaller than the smallest difference we've found so far
        if (difference < smallestDifference) {
            // If so, update the smallestDifference and the closestItem
            smallestDifference = difference;
            closestItem = item;
        }
    });
    // After checking all items, return the closestItem found
    return closestItem;
}
